import { AutocompleteValue, PopoverOrigin } from '@mui/material';
import { sortBy } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { User } from 'src/constants';
import { SelectDropDown } from 'src/legacy/components/Select/SelectDropdown';
import { SelectOption } from 'src/legacy/components/Select/types';
import { RootState } from 'src/store';
import { AssigneeDropDownOptions } from 'src/store/clients/types';

const useStyles = makeStyles()((theme) => ({
  popover: {
    marginTop: theme.spacing(0.5),
  },
}));

type GroupMembersDropDownProps<IsMultiple extends boolean = true> = {
  companyId?: string;
  memberIDs: string[];
  assigneeDropDownId?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: React.ComponentProps<typeof SelectDropDown>['onClose'];
  noOptionsText?: string;
  placeholder: string;
  disableCloseOnSelect: boolean;
  width?: string | number;
  anchorOrigin?: PopoverOrigin;
  disabledIDsForSelection?: string[] | undefined;
  transformOrigin?: PopoverOrigin;
  isMultiple: IsMultiple;
  onChange: (
    val: AutocompleteValue<
      SelectOption | AssigneeDropDownOptions,
      IsMultiple,
      false,
      false
    >,
  ) => void;
};

export function GroupMembersDropDown<IsMultiple extends boolean = true>({
  companyId,
  memberIDs,
  assigneeDropDownId,
  open,
  anchorEl,
  onClose,
  noOptionsText = 'No options',
  placeholder = '',
  isMultiple,
  disabledIDsForSelection = undefined,
  onChange,
  width: menuWidth,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
  },
  transformOrigin,
  ...autocompleteOptions
}: GroupMembersDropDownProps<IsMultiple>) {
  const { classes } = useStyles();
  const clientUsers = useSelector(
    (state: RootState) => state.clients.activeClients,
  );
  // holds the dropdown values which are displayed to the user
  const [options, setOptions] = React.useState<AssigneeDropDownOptions[]>([]);
  const [pendingValue, setPendingValue] = React.useState<any>([]);

  React.useEffect(() => {
    if (clientUsers) {
      const memberIdsSet = new Set(memberIDs);
      const memberUsers = clientUsers.filter((u) => memberIdsSet.has(u.id));

      let filteredCompanyUsers: User[] = [];
      if (companyId) {
        filteredCompanyUsers = clientUsers.filter((u) =>
          u.fields.companyIds && u.fields.companyIds.length > 0
            ? u.fields.companyIds.includes(companyId)
            : u.fields.companyId === companyId,
        );
      } else {
        const inferredCompanyId =
          memberUsers && memberUsers.length > 0
            ? memberUsers.at(0)?.fields.companyId
            : '';
        filteredCompanyUsers = clientUsers.filter(
          (u) => u.fields.companyId === inferredCompanyId,
        );
      }

      const clientUsersStaged: Array<AssigneeDropDownOptions> =
        filteredCompanyUsers.map((clientUser) => {
          const {
            givenName,
            familyName,
            avatarImageUrl,
            fallbackColor,
            roles,
          } = clientUser.fields;
          return {
            label: `${givenName} ${familyName}`,
            avatarUrl: avatarImageUrl,
            id: clientUser.id,
            fallbackColor,
            type: 'client',
            role: roles && roles.length > 0 ? (roles.at(0) ?? '') : '',
          };
        });

      const sortedInternalOptions = sortBy(clientUsersStaged, 'label');
      setOptions(sortedInternalOptions);
    }
  }, [clientUsers, companyId]);

  React.useEffect(() => {
    if (isMultiple && memberIDs) {
      const memberIdsSet = new Set(memberIDs);
      const pendingValueStaged = options.filter((option) =>
        memberIdsSet.has(option.id),
      );
      setPendingValue(pendingValueStaged);
    }
  }, [isMultiple, memberIDs, options]);

  const isOptionDisabled = (option: SelectOption) => {
    if (pendingValue.length === 1 && pendingValue.at(0).id === option.id) {
      // don't allow user to de-select all options
      return true;
    }
    if (
      disabledIDsForSelection &&
      disabledIDsForSelection.includes(option.id)
    ) {
      return true;
    }
    return false;
  };

  return (
    <SelectDropDown<AssigneeDropDownOptions, IsMultiple>
      width={menuWidth}
      isMultiple={isMultiple}
      id={assigneeDropDownId}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      onClose={(event, reason) => {
        event.preventDefault();
        onClose(event, reason);
      }}
      value={pendingValue}
      onChange={(_, newValue) => {
        if (newValue) {
          onChange(newValue);
          setPendingValue(newValue);
        }
      }}
      getOptionDisabled={(option) => isMultiple && isOptionDisabled(option)}
      noOptionsText={noOptionsText}
      options={options}
      placeholder={placeholder}
      transformOrigin={transformOrigin}
      popoverClassname={classes.popover}
      {...autocompleteOptions}
    />
  );
}
